import { useEffect, useRef, useState } from 'react'
import { Play, Pause, Loader2 } from 'lucide-react'
import { recordingsApi } from '../api/recordings'

/**
 * Inline audio player for a single call recording. Lazy-loads the audio (as an
 * authenticated Blob) on first Play, then exposes native controls + a speed toggle.
 * Loading is deferred to the first Play so opening a table of recordings doesn't
 * fetch every file at once (which trips the API rate limit). Stream-only: there
 * is no download affordance.
 */
export function RecordingPlayer({ companyId, recordingId }: { companyId: string; recordingId: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const objectUrlRef = useRef<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [rate, setRate] = useState(1)
  const [error, setError] = useState(false)

  // Revoke the object URL on unmount to avoid leaking memory.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  async function ensureLoaded() {
    if (loaded || loading) return
    setLoading(true)
    setError(false)
    try {
      const res = await recordingsApi.fetchBlob(companyId, recordingId)
      const url = URL.createObjectURL(res.data as Blob)
      objectUrlRef.current = url
      if (audioRef.current) audioRef.current.src = url
      setLoaded(true)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  async function toggle() {
    await ensureLoaded()
    const el = audioRef.current
    if (!el || error) return
    if (el.paused) {
      el.playbackRate = rate
      try { await el.play(); setPlaying(true) } catch { /* ignored */ }
    } else {
      el.pause()
      setPlaying(false)
    }
  }

  function cycleRate() {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1
    setRate(next)
    if (audioRef.current) audioRef.current.playbackRate = next
  }

  return (
    <div className="flex items-center gap-2 mt-1">
      <button type="button" className="btn btn-ghost btn-sm" onClick={toggle} disabled={loading}>
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : playing ? (
          <Pause className="w-3.5 h-3.5" />
        ) : (
          <Play className="w-3.5 h-3.5" />
        )}
        <span className="ml-1">{error ? 'Unavailable' : loaded ? (playing ? 'Pause' : 'Play') : 'Play recording'}</span>
      </button>
      <audio
        ref={audioRef}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        controls={loaded && !error}
        className={loaded && !error ? 'h-8' : 'hidden'}
        preload="none"
      />
      {loaded && !error && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={cycleRate}>
          {rate}×
        </button>
      )}
    </div>
  )
}
